import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { mockData } from '../../data/mock';
import {
  BarChart3,
  Video,
  MessageSquare,
  Settings,
  LogOut,
  Eye,
  Edit,
  HelpCircle,
  Mail,
  Monitor
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({
    totalVideos: 0,
    totalWebProjects: 0,
    totalMessages: 0,
    recentMessages: []
  });
  const [recentVideos, setRecentVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load videos
      const videosResponse = await axios.get(`${API}/admin/videos?limit=5`);
      setRecentVideos(videosResponse.data.data);
      setStats(prev => ({ ...prev, totalVideos: videosResponse.data.total }));
      
      // Load web projects
      const webProjectsResponse = await axios.get(`${API}/admin/web-projects?limit=5`);
      setStats(prev => ({ ...prev, totalWebProjects: webProjectsResponse.data.total }));

      // Load messages
      const messagesResponse = await axios.get(`${API}/admin/contact-messages?limit=5`);
      setStats(prev => ({ 
        ...prev, 
        totalMessages: messagesResponse.data.total,
        recentMessages: messagesResponse.data.data
      }));
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#f97316' }}></div>
          <p className="text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img 
                src={mockData.hero.logoIconLight}
                alt="C² Productions" 
                className="h-8 w-8"
              />
              <h1 className="text-xl font-semibold text-gray-900">
                Administration C² Productions
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Bonjour, {user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Quick Actions */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Actions Rapides</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <button
                onClick={() => window.location.href = '/admin/web-projects'}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Monitor className="h-6 w-6 text-orange-500 mb-2" />
                <span className="text-sm font-medium text-gray-900">Sites</span>
              </button>

              <button
                onClick={() => window.location.href = '/admin/videos'}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Video className="h-6 w-6 text-orange-500 mb-2" />
                <span className="text-sm font-medium text-gray-900">Vidéos</span>
              </button>
              
              <button
                onClick={() => window.location.href = '/admin/categories'}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Settings className="h-6 w-6 text-orange-500 mb-2" />
                <span className="text-sm font-medium text-gray-900">Catégories</span>
              </button>

              <button
                onClick={() => window.location.href = '/admin/web-categories'}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Settings className="h-6 w-6 text-orange-500 mb-2" />
                <span className="text-sm font-medium text-gray-900">Catégories Web</span>
              </button>
              
              <button
                onClick={() => window.location.href = '/admin/faq'}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <HelpCircle className="h-6 w-6 text-orange-500 mb-2" />
                <span className="text-sm font-medium text-gray-900">FAQ</span>
              </button>
              
              <button
                onClick={() => window.location.href = '/admin/testimonials'}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <MessageSquare className="h-6 w-6 text-orange-500 mb-2" />
                <span className="text-sm font-medium text-gray-900">Avis</span>
              </button>
              
              <button
                onClick={() => window.location.href = '/admin/messages'}
                className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Mail className="h-6 w-6 text-orange-500 mb-2" />
                <span className="text-sm font-medium text-gray-900">Messages</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Monitor className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Sites
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalWebProjects}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Video className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Vidéos
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalVideos}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MessageSquare className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Messages Reçus
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalMessages}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart3 className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Vues Portfolio
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      -
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Videos */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Vidéos Récentes
              </h3>
              <div className="space-y-4">
                {recentVideos.length > 0 ? (
                  recentVideos.map((video) => (
                    <div key={video.id} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="h-12 w-20 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {video.title}
                        </p>
                        <p className="text-sm text-gray-500">
                          {video.category} • {video.date}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => window.open(`https://youtube.com/watch?v=${video.youtube_id}`, '_blank')}
                          className="text-gray-400 hover:text-gray-600"
                          title="Voir la vidéo"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => window.location.href = `/admin/videos?edit=${video.id}`}
                          className="text-gray-400 hover:text-gray-600"
                          title="Modifier la vidéo"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">Aucune vidéo trouvée</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Messages */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Messages Récents
              </h3>
              <div className="space-y-4">
                {stats.recentMessages.length > 0 ? (
                  stats.recentMessages.map((message) => (
                    <div key={message.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-sm font-medium text-gray-900">
                          {message.name}
                        </p>
                        <span className="text-xs text-gray-500">
                          {new Date(message.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        {message.subject}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {message.message}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">Aucun message trouvé</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
